import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Eye, Cpu, Shield, Camera, HardDrive, CheckCircle, Users, Car, 
  Lock, Play, Filter, Database, Phone, FileText, Zap, Sun, Volume2, ArrowRight
} from 'lucide-react';
import QuoteForm from '../components/QuoteForm';
import CardCarousel from '../components/ui/CardCarousel';

const SuperiorVideoPage = () => {
  // Images officielles Ajax (utilisées avec autorisation)
  const AJAX_IMAGES = {
    heroBanner: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_video_surveillance_xl_40d78b252b%402.jpg",
    bulletcam: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_bulletcam_hlvf_black_3b73a11495%402.png",
    domecam: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_domecam_hlvf_black_6459f31e3d%402.png",
    turretcam: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_turretcam_hlvf_black_484e6470ab%402.png",
    nvrH2D: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_nvr_h2dai_black_e75a5cf41c%402.png",
    nvrH2D2G: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_nvr_h2dai2gac_black_25085a5aee%402.png",
    aiView: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fcameras_hlvf_ai_view_0dc05637cd%401.jpg",
    jetSparrow: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fjet_sparrow_md_3341c29cb1%402.jpg",
    videoWall: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fvideo_wall_all_cameras_with_tv_dark_md_c4078d56b2%401.jpg",
    varifocalLens: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_cameras_varifocal_lens_9f5269ca4c%401.png",
    colorNight: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_cameras_colors_in_the_dark_sm_1f87637e11%401.png",
    audio: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fbulletcam_audio_4cbd31a7f2%402.jpg",
  };

  // TOUS les produits caméras (6 modèles)
  const allCameras = [
    {
      name: "Superior BulletCam HLVF (4 Mp)",
      image: AJAX_IMAGES.bulletcam,
      description: "Caméra IP filaire avec IA, objectif varifocal motorisé P-Iris 2,8–12 mm, éclairage hybride, True WDR.",
      specs: ["4 Mp", "P-Iris", "IA 4×", "IP67"],
    },
    {
      name: "Superior BulletCam HLVF (8 Mp)",
      image: AJAX_IMAGES.bulletcam,
      description: "Version 8 Mp pour des détails encore plus fins et un zoom numérique optimal.",
      specs: ["8 Mp", "P-Iris", "IA 4×", "IP67"],
    },
    {
      name: "Superior DomeCam HLVF (4 Mp)",
      image: AJAX_IMAGES.domecam,
      description: "Caméra dôme discrète avec IA embarquée, idéale pour les espaces commerciaux.",
      specs: ["4 Mp", "Discret", "2 micros", "IK10"],
    },
    {
      name: "Superior DomeCam HLVF (8 Mp)",
      image: AJAX_IMAGES.domecam,
      description: "Version 8 Mp pour une résolution maximale dans les environnements exigeants.",
      specs: ["8 Mp", "Discret", "2 micros", "IK10"],
    },
    {
      name: "Superior TurretCam HLVF (4 Mp)",
      image: AJAX_IMAGES.turretcam,
      description: "Caméra tourelle polyvalente pour une couverture optimale en intérieur/extérieur.",
      specs: ["4 Mp", "IR 40m", "WDR 130dB", "PoE"],
    },
    {
      name: "Superior TurretCam HLVF (8 Mp)",
      image: AJAX_IMAGES.turretcam,
      description: "Version 8 Mp avec tous les avantages de la gamme Superior.",
      specs: ["8 Mp", "IR 40m", "WDR 130dB", "PoE"],
    },
  ];

  // TOUS les NVR (7 modèles)
  const allNVRs = [
    {
      name: "Superior NVR H2DAI8PAC (8-ch)",
      image: AJAX_IMAGES.nvrH2D,
      description: "Enregistreur 8 canaux avec 8 ports PoE intégrés.",
      specs: ["8 canaux", "8 ports PoE", "HDMI 4K", "2×24 To"],
    },
    {
      name: "Superior NVR H2DAI8PAC (16-ch)",
      image: AJAX_IMAGES.nvrH2D,
      description: "Enregistreur 16 canaux avec 8 ports PoE.",
      specs: ["16 canaux", "8 ports PoE", "HDMI 4K", "2×24 To"],
    },
    {
      name: "Superior NVR H2DAI16PAC (16-ch)",
      image: AJAX_IMAGES.nvrH2D,
      description: "Enregistreur 16 canaux avec 16 ports PoE pour grandes installations.",
      specs: ["16 canaux", "16 ports PoE", "HDMI 4K", "2×24 To"],
    },
    {
      name: "Superior NVR H2DAI16PAC (32-ch)",
      image: AJAX_IMAGES.nvrH2D,
      description: "Enregistreur 32 canaux avec 16 ports PoE pour projets d'envergure.",
      specs: ["32 canaux", "16 ports PoE", "HDMI 4K", "2×24 To"],
    },
    {
      name: "Superior NVR H2DAI2GAC (8-ch)",
      image: AJAX_IMAGES.nvrH2D2G,
      description: "Enregistreur 8 canaux avec 2× Gigabit Ethernet.",
      specs: ["8 canaux", "2× Gigabit", "Sans PoE", "2×24 To"],
    },
    {
      name: "Superior NVR H2DAI2GAC (16-ch)",
      image: AJAX_IMAGES.nvrH2D2G,
      description: "Enregistreur 16 canaux avec 2× Gigabit Ethernet.",
      specs: ["16 canaux", "2× Gigabit", "Sans PoE", "2×24 To"],
    },
    {
      name: "Superior NVR H2DAI2GAC (32-ch)",
      image: AJAX_IMAGES.nvrH2D2G,
      description: "Enregistreur 32 canaux avec 2× Gigabit pour réseaux enterprise.",
      specs: ["32 canaux", "2× Gigabit", "Sans PoE", "2×24 To"],
    },
  ];

  // Technologies clés avec liens vers pages dédiées
  const keyTechnologies = [
    {
      icon: Eye,
      title: "Objectif varifocal P-Iris",
      description: "Zoom optique 2,8-12mm avec autofocus. Exposition parfaite grâce au diaphragme motorisé.",
      color: "blue",
      link: "/tech/varifocal",
      image: AJAX_IMAGES.varifocalLens,
    },
    {
      icon: Cpu,
      title: "IA 4× plus puissante",
      description: "Détection précise des personnes, animaux et véhicules. Notifications ciblées.",
      color: "purple",
      link: "/tech/ia",
      image: AJAX_IMAGES.aiView,
    },
    {
      icon: Sun,
      title: "Éclairage hybride",
      description: "Vision couleur même la nuit. IR jusqu'à 60m + lumière blanche pour dissuasion.",
      color: "yellow",
      link: "/tech/eclairage",
      image: AJAX_IMAGES.colorNight,
    },
    {
      icon: Volume2,
      title: "Audio bidirectionnel",
      description: "2 microphones + haut-parleur intégrés. Dissuasion active à distance.",
      color: "green",
      link: "/tech/audio",
      image: AJAX_IMAGES.audio,
    },
  ];

  return (
    <div className="min-h-screen py-8 md:py-12 bg-black" data-testid="superior-video-page">
      
      {/* Hero Section */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12 md:mb-16">
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden">
          <img 
            src={AJAX_IMAGES.heroBanner}
            alt="Vidéosurveillance Superior Ajax Systems"
            className="w-full h-[300px] md:h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent flex items-center">
            <div className="px-6 md:px-12 max-w-2xl">
              <p className="text-blue-400 text-sm font-medium tracking-wide uppercase mb-3">Ligne Superior • IA embarquée</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
                Vidéosurveillance <span className="text-blue-400">Professionnelle</span>
              </h1>
              <p className="text-sm md:text-base text-white/50 mb-6 leading-relaxed font-light">
                Caméras et enregistreurs conçus pour les clients exigeants. 
                Qualité visuelle exceptionnelle, IA enrichie, sécurité sans compromis.
              </p>
              <div className="flex flex-wrap gap-3">
                <a 
                  href="#devis" 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium rounded-full hover:bg-white/90 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Demander un devis
                </a>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 text-white font-medium rounded-full border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Nous contacter
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Badges de confiance */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12 md:mb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Shield, text: "Garantie 4 ans", color: "text-emerald-400" },
            { icon: Cpu, text: "IA embarquée", color: "text-blue-400" },
            { icon: Lock, text: "Conforme RGPD", color: "text-green-400" },
            { icon: Phone, text: "Devis sous 24h", color: "text-purple-400" },
          ].map((badge, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-4 flex items-center gap-3">
              <badge.icon className={`w-5 h-5 ${badge.color} flex-shrink-0`} />
              <span className="text-white/70 text-sm font-medium">{badge.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Section: Technologies clés avec liens */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12 md:mb-16">
        <div className="text-center mb-8 md:mb-10">
          <p className="text-blue-400 text-sm font-medium uppercase tracking-wider mb-2">Technologie de pointe</p>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
            Conçue pour se démarquer
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-sm md:text-base font-light">
            Cliquez sur chaque technologie pour découvrir tous les détails et avantages.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {keyTechnologies.map((tech, i) => (
            <Link 
              key={i}
              to={tech.link}
              className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 group"
            >
              <div className="h-32 overflow-hidden">
                <img 
                  src={tech.image} 
                  alt={tech.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className={`w-10 h-10 rounded-full bg-${tech.color}-500/10 flex items-center justify-center`}>
                    <tech.icon className={`w-5 h-5 text-${tech.color}-400`} />
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-blue-400 transition-colors" />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{tech.title}</h3>
                <p className="text-sm text-white/60 line-clamp-2">{tech.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Section: Carousel des caméras */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12 md:mb-16">
        <CardCarousel
          title="Caméras filaires Superior"
          subtitle="Objectif P-Iris • IA embarquée • Audio bidirectionnel"
          showDots={true}
          showArrows={true}
        >
          {allCameras.map((camera, i) => (
            <div 
              key={i}
              className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 group h-full"
            >
              <div className="bg-[#0a0a0a] p-4 flex items-center justify-center h-40">
                <img 
                  src={camera.image} 
                  alt={camera.name}
                  className="h-full w-auto object-contain group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-white mb-1 line-clamp-1">{camera.name}</h3>
                <p className="text-xs text-white/60 mb-3 line-clamp-2">{camera.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {camera.specs.map((spec, j) => (
                    <span key={j} className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded-full">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </CardCarousel>
      </section>

      {/* Section: Carousel des NVR */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12 md:mb-16">
        <CardCarousel
          title="Enregistreurs NVR Superior"
          subtitle="Jusqu'à 32 canaux • 2×24 To • IA intégrée"
          showDots={true}
          showArrows={true}
        >
          {allNVRs.map((nvr, i) => (
            <div 
              key={i}
              className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 group h-full"
            >
              <div className="bg-[#0a0a0a] p-4 flex items-center justify-center h-32">
                <img 
                  src={nvr.image} 
                  alt={nvr.name}
                  className="h-full w-auto object-contain group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-white mb-1 line-clamp-1">{nvr.name}</h3>
                <p className="text-xs text-white/60 mb-3 line-clamp-2">{nvr.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {nvr.specs.map((spec, j) => (
                    <span key={j} className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded-full">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </CardCarousel>
      </section>

      {/* Section: JetSparrow & Applications */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12 md:mb-16">
        <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
          {/* JetSparrow */}
          <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
            <img 
              src={AJAX_IMAGES.jetSparrow}
              alt="Technologie JetSparrow"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6 text-blue-400" />
                <h3 className="font-heading text-lg font-bold text-white uppercase">Technologie JetSparrow</h3>
              </div>
              <p className="text-sm text-white/50 mb-4">
                Navigation ultra-rapide dans les archives. Lecture fluide, filtres par type d'objet IA, 
                exportation de clips en quelques secondes.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Play, title: "Flux en direct" },
                  { icon: Filter, title: "Filtres IA" },
                  { icon: Database, title: "Archives rapides" },
                  { icon: Lock, title: "Chiffrement TLS" },
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white">
                    <feature.icon className="w-4 h-4 text-blue-400" />
                    <span>{feature.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mur vidéo */}
          <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
            <img 
              src={AJAX_IMAGES.videoWall}
              alt="Mur vidéo personnalisable"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-emerald-400" />
                <h3 className="font-heading text-lg font-bold text-white uppercase">Sécurité & Confidentialité</h3>
              </div>
              <p className="text-sm text-white/50 mb-4">
                Protection des données conforme RGPD et NDAA. Chiffrement TLS, authentification 2FA.
              </p>
              <div className="space-y-2">
                {[
                  "Flux vidéo chiffré TLS",
                  "Authentification sans mot de passe",
                  "Export sécurisé avec signature",
                  "Contrôle d'accès utilisateur",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Formulaire de devis */}
      <section id="devis" className="max-w-[1400px] mx-auto px-4 md:px-12 scroll-mt-24">
        <div className="bg-gradient-to-br from-fgs-surface via-fgs-surface to-blue-900/20 border border-white/5 rounded-2xl p-6 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <p className="text-emerald-400 text-sm font-medium uppercase tracking-wider mb-2">Partenaire accrédité Ajax</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                Demandez votre étude vidéo
              </h2>
              <p className="text-white/50 mb-6 text-sm md:text-base">
                Nos experts dimensionnent votre installation Superior selon vos besoins : 
                nombre de caméras, zones à couvrir, stockage requis.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  "Audit de site et plan d'implantation",
                  "Configuration IA personnalisée",
                  "Installation par techniciens certifiés Ajax",
                  "Formation à l'utilisation",
                  "Garantie 4 ans FGS incluse",
                  "Maintenance et SAV réactif",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-black/40/50 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-white/50 mb-2">
                  Les produits Superior sont réservés aux partenaires accrédités Ajax Systems.
                </p>
                <p className="text-sm text-white font-medium">
                  FGS est partenaire certifié pour la vente, l'installation et la maintenance.
                </p>
              </div>
            </div>
            
            <div>
              <QuoteForm category="video" />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SuperiorVideoPage;
